import { WhatsAppHealth } from "@superagents/types";

/**
 * Gate de PRONTIDÃO de disparo (SA-1170/1196). Um único derivador decide se o
 * setor pode disparar agora e, quando não pode, com qual copy/CTA — consumido
 * pelo builder (bloqueio full-screen), pela lista (banner + lock do CTA) e pelo
 * aviso de qualidade.
 *
 * Ordem de bloqueio (mais fundamental primeiro):
 * - **Não-oficial** (domestic legado): disparo exige a API oficial da Meta. É a
 *   fonte autoritativa via `sectorType` — o snapshot pode nem existir para
 *   domestic. (A conexão domestic está em extinção; aqui vira só um estado de
 *   "conecte a API oficial", não um fluxo próprio.)
 * - **Desconectado** (`connected: false`): com a conexão fora do ar não há como
 *   enviar — reconectar é o pré-requisito.
 * - **Pendência de pagamento** (131042/141006 — o 141006 segue o status da
 *   Meta no snapshot e pode vir como warning, mas para disparo em massa é gate).
 * - **`canSendMessage: BLOCKED`** — a Meta está recusando envios.
 *
 * AVISA (não bloqueia): qualidade RED — a Meta não recusa, mas disparar piora a
 * avaliação do número. LIMITED (throughput) não bloqueia. Snapshot ausente
 * (carregando/endpoint falhou) para setor cloud: fail-open — não punir erro
 * transitório; o erro real da Meta ainda aparece no envio.
 */

// Ordem = prioridade da copy quando ambos aparecem. Mesmos códigos de
// PAYMENT_ISSUE_CODES em backstrapi/src/services/whatsapp-health/paymentIssueCodes.ts.
const BLOCKING_PAYMENT_CODES = ["131042", "141006"] as const;

const NOT_OFFICIAL_COPY: WhatsAppHealth.HealthIssueCopy = {
  severity: "error",
  title: "Conecte o WhatsApp API oficial",
  description:
    "O disparo em massa usa a API oficial do WhatsApp (Meta). Conecte este número pela API oficial para criar e enviar disparos.",
};

const DISCONNECTED_COPY: WhatsAppHealth.HealthIssueCopy = {
  severity: "error",
  title: "WhatsApp desconectado",
  description:
    "A conexão deste WhatsApp está fora do ar. Reconecte para voltar a disparar.",
};

const BLOCKED_FALLBACK_COPY: WhatsAppHealth.HealthIssueCopy = {
  severity: "error",
  title: "Envio de mensagens bloqueado pela Meta",
  description:
    "A Meta está recusando envios desta conexão no momento. Verifique o status da conexão para ver os detalhes da pendência.",
};

export type SectorConnectionType = "cloud" | "domestic";

export type CampaignHealthGateView = {
  /** Impede iniciar/disparar (hard). */
  blocked: boolean;
  /** Copy pt-BR da pendência que causou o bloqueio (catálogo healthIssues). */
  copy: WhatsAppHealth.HealthIssueCopy | null;
  /** Aviso não-bloqueante no builder. */
  qualityRed: boolean;
};

export function deriveCampaignHealthGate(
  snapshot: WhatsAppHealth.WhatsAppHealthSnapshot | null | undefined,
  sectorType?: SectorConnectionType
): CampaignHealthGateView {
  // Não-oficial (domestic): sectorType é autoritativo e sempre presente — o
  // snapshot pode nem existir. Bloqueia com o estado "conecte a API oficial".
  if (sectorType === "domestic") {
    return { blocked: true, copy: NOT_OFFICIAL_COPY, qualityRed: false };
  }

  if (!snapshot) {
    // Carregando / endpoint falhou: fail-open.
    return { blocked: false, copy: null, qualityRed: false };
  }

  if (snapshot.apiType !== "official") {
    return { blocked: true, copy: NOT_OFFICIAL_COPY, qualityRed: false };
  }

  const qualityRed = snapshot.qualityRating === "RED";

  // Desconectado: `=== false` explícito — snapshot cacheado antigo sem o campo
  // (undefined) não deve bloquear (fail-open).
  if (snapshot.connected === false) {
    return { blocked: true, copy: DISCONNECTED_COPY, qualityRed };
  }

  const paymentCheck = BLOCKING_PAYMENT_CODES.map((code) =>
    snapshot.checks.find((check) => check.code === code)
  ).find(Boolean);

  if (paymentCheck) {
    return {
      blocked: true,
      copy: WhatsAppHealth.getHealthCopy(
        paymentCheck.code,
        paymentCheck.rawTitle,
        paymentCheck.rawDescription,
        paymentCheck.severity
      ),
      qualityRed,
    };
  }

  if (snapshot.canSendMessage === "BLOCKED") {
    const errorCheck = snapshot.checks.find(
      (check) => check.severity === "error"
    );
    return {
      blocked: true,
      copy: errorCheck
        ? WhatsAppHealth.getHealthCopy(
            errorCheck.code,
            errorCheck.rawTitle,
            errorCheck.rawDescription,
            errorCheck.severity
          )
        : BLOCKED_FALLBACK_COPY,
      qualityRed,
    };
  }

  return { blocked: false, copy: null, qualityRed };
}
